import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import type { CardAction, CardResponse } from '@omni/ai/features/card.schema';
import type { TokenUsage } from '@omni/ai/transports/ai-transport';
import { AiToolRegistryService } from '@omni/ai/tools/tool-registry.service';
import { NovaSectionComponent } from './nova-section.component';
import { CardActionsComponent } from './card-actions.component';
import { NovaFeedbackFooterComponent, NovaFeedbackEvent } from '../nova-feedback-footer/nova-feedback-footer.component';

type CardState = 'collapsed' | 'minimized' | 'maximized';

/** The record kind each record-opening client tool opens. */
const RECORD_TOOLS: Readonly<Record<string, 'contact' | 'account'>> = {
  view_contact: 'contact',
  view_account: 'account',
};

/**
 * The record a tool action opens: `view_contact:<contact id>` or `view_account:<account id>`.
 * Null for any other tool, or when the action names no record id.
 */
export function openedRecord(
  value: string,
): { kind: 'contact' | 'account'; id: string } | null {
  const separator = value.indexOf(':');
  if (separator < 0) return null;
  const kind = RECORD_TOOLS[value.slice(0, separator).trim()];
  const id = value.slice(separator + 1).trim();
  return kind && id ? { kind, id } : null;
}

/**
 * Composite root for the unified Nova card. Walks `card.sections` through the
 * `<nova-section>` dispatcher; renders the optional title, card-level action
 * chips, and a disclaimer footer.
 *
 * When `collapsible` is true the shell owns the brief's presentation state
 * machine — collapse / minimize / maximize, the ResizeObserver read-more
 * evaluation, and the outside-click-to-minimize listener. State ONLY; data
 * loading stays in the host.
 *
 * `action` is fired by card-level + per-insight chips; the shell dispatches
 * `kind:'tool'` to the named client `@AITool` via {@link AiToolRegistryService}
 * and re-emits `kind:'prompt'` for the host to send as the next user turn.
 */
@Component({
  selector: 'nova-card',
  standalone: true,
  imports: [CommonModule, IonicModule, TranslateModule, NovaSectionComponent, CardActionsComponent, NovaFeedbackFooterComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './nova-card.component.html',
  styleUrls: ['./nova-card.component.scss'],
})
export class NovaCardComponent implements OnInit, OnDestroy {
  @Input() card: CardResponse | null = null;

  /** Opt into the collapse/minimize/maximize affordance (the brief surface). */
  @Input() collapsible = false;

  /** Initial state when collapsible; spotlight starts minimized, profiles collapsed. */
  @Input() startMinimized = false;

  @Input() loading = false;
  @Input() disclaimer: string | null = null;
  @Input() offline = false;

  /**
   * Localized message shown in the offline body. Only the brief surface binds
   * this; other surfaces never enter the offline branch.
   */
  @Input() offlineMessage: string | null = null;

  /**
   * Localized fallback shown when `card.title` is absent (e.g. the brief
   * surface while loading or when the agent omits a title). Other surfaces
   * pass an explicit `card.title` so this is effectively brief-only.
   */
  @Input() fallbackTitle: string | null = null;

  /**
   * When false, card-level action chips are suppressed. Defaults to true so
   * brief and insights surfaces keep their chips. The assistant chat surface
   * sets this to false on every message except the most recent, so only the
   * latest turn shows action chips.
   */
  @Input() actionsActive = true;

  /** Responses API id of the turn that produced `card`. Drives the feedback footer — the footer
   *  renders only when this is set (e.g. never for restored assistant-chat history). */
  @Input() responseId?: string | null = null;
  /** Token usage for the turn that produced `card`, shown as a chip next to the vote buttons. */
  @Input() usage?: TokenUsage | null = null;

  /** A record to open: re-emitted from `<card-entities>`, or from a tool action that opens a record
   *  (`view_contact`, `view_account`), so the host opens it the same way for both. */
  @Output() openEntity = new EventEmitter<{ kind: 'contact' | 'account'; id: string }>();

  /** Fired for `kind:'prompt'` chips after the shell handles `kind:'tool'` itself. */
  @Output() action = new EventEmitter<CardAction>();

  /** A thumbs vote from the feedback footer — the host submits it via AssistantFeedbackService. */
  @Output() feedback = new EventEmitter<NovaFeedbackEvent>();

  /** Result of a `kind:'tool'` dispatch — `{name, output}`. */
  @Output() toolResult = new EventEmitter<{ name: string; output: unknown }>();

  /**
   * Fires whenever the collapse state crosses the collapsed↔open boundary (only
   * meaningful when `collapsible`). Lets a host lazy-load card data on first open
   * instead of while the card sits collapsed. `true` = open (minimized/maximized).
   */
  @Output() expandedChange = new EventEmitter<boolean>();

  readonly #el = inject(ElementRef<HTMLElement>);
  readonly #cd = inject(ChangeDetectorRef);
  readonly #tools = inject(AiToolRegistryService);

  #resizeObserver: ResizeObserver | null = null;

  #state: CardState = 'collapsed';
  hasReadMore = false;

  @HostBinding('class.collapsible')
  get isCollapsible(): boolean {
    return this.collapsible;
  }

  @HostBinding('class.collapsed')
  get isCollapsed(): boolean {
    return this.collapsible && this.#state === 'collapsed';
  }

  get isMinimized(): boolean {
    return this.collapsible && this.#state === 'minimized';
  }

  @HostBinding('class.maximized')
  get isMaximized(): boolean {
    return this.collapsible && this.#state === 'maximized';
  }

  ngOnInit(): void {
    this.#state = this.startMinimized ? 'minimized' : 'collapsed';
    if (this.collapsible && this.#state !== 'collapsed') {
      // Surface the initial open state so a host can lazy-load on first render.
      this.expandedChange.emit(true);
    }
    if (this.collapsible) {
      this.#resizeObserver = new ResizeObserver(() => {
        this.#evaluateReadMore();
        this.#cd.markForCheck();
      });
      this.#resizeObserver.observe(this.#el.nativeElement);
    }
  }

  ngOnDestroy(): void {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  get hasContent(): boolean {
    return !!this.card && this.card.sections.length > 0;
  }

  trackSection(_i: number, section: CardResponse['sections'][number]): string {
    const title = 'title' in section ? section.title ?? '' : '';
    return section.type + ':' + title;
  }

  // ---- collapse / minimize / maximize state machine -------------------------

  /**
   * Return the card to its initial state. Call from the host's `ngOnChanges`
   * when the bound record switches (contactId / accountId) so stale
   * collapse/maximize state does not leak across records.
   */
  resetState(): void {
    this.#state = this.startMinimized ? 'minimized' : 'collapsed';
    this.hasReadMore = false;
    this.#cd.markForCheck();
  }

  toggleHeader(): void {
    if (!this.collapsible) return;
    if (this.#state === 'collapsed') {
      this.#setState('minimized');
    } else {
      this.#setState('collapsed');
    }
  }

  toggleMaximize(): void {
    if (this.#state === 'minimized') {
      this.#setState('maximized');
    } else if (this.#state === 'maximized') {
      this.#setState('minimized');
    }
  }

  @HostListener('document:touchend', ['$event'])
  @HostListener('document:mouseup', ['$event'])
  onDocumentInteraction(event: Event): void {
    if (this.collapsible && this.#state === 'maximized') {
      if (!this.#el.nativeElement.contains(event.target as Node)) {
        this.#setState('minimized');
      }
    }
  }

  #setState(next: CardState): void {
    const wasOpen = this.#state !== 'collapsed';
    const isOpen = next !== 'collapsed';
    this.#state = next;
    if (wasOpen !== isOpen) this.expandedChange.emit(isOpen);
    this.#cd.markForCheck();
    requestAnimationFrame(() => {
      this.#evaluateReadMore();
      this.#cd.markForCheck();
    });
  }

  #evaluateReadMore(): void {
    const host = this.#el.nativeElement as HTMLElement;
    const clamped = host.querySelector('.nova-card__clamp') as HTMLElement | null;
    if (!clamped) return;
    this.hasReadMore = clamped.scrollHeight > clamped.clientHeight + 1;
  }

  // ---- action dispatch -----------------------------------------------------

  async onAction(a: CardAction): Promise<void> {
    if (a.kind === 'tool') {
      const record = openedRecord(a.value);
      if (record) {
        this.openEntity.emit(record);
        return;
      }
      this.toolResult.emit(await this.#tools.executeAction(a.value));
      return;
    }
    // kind: 'prompt' — the host sends `value` as the next user turn.
    this.action.emit(a);
  }
}
